import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ArrowRight, Mail, MessageSquare, Building } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const { t } = useTranslation('landing');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    teamSize: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
    } catch (err) {
      console.error('Failed to send contact form:', err);
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <SEO
        title={t('contact.seo.title')}
        description={t('contact.seo.description')}
        canonical="/contact"
      />
      <Navbar />

      <main className="pt-20 pb-24">
        <section className="max-w-7xl mx-auto px-4 py-16">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left side */}
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {t('contact.headline')}
              </h1>
              <p className="text-xl text-gray-400 mb-8">
                {t('contact.subtitle')}
              </p>

              {/* A "Live chat, Mon-Fri 9am-6pm ET" card used to sit between
                  these two. There is no live chat in the product - no Intercom,
                  Crisp, Drift or anything comparable is loaded anywhere - so it
                  promised a channel that does not exist. Removed.
                  The office was listed as "San Francisco, CA"; the company is
                  registered and operates in Košice, Slovakia. */}
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{t('contact.channels.email_title')}</h3>
                    <p className="text-gray-400">{t('contact.channels.email_body')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <Building className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{t('contact.channels.hq_title')}</h3>
                    <p className="text-gray-400">{t('contact.channels.hq_location')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side - Form */}
            <div className="bg-white/5 rounded-2xl border border-gray-800 p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">{t('contact.success.title')}</h3>
                  <p className="text-gray-400">{t('contact.success.body')}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label className="text-gray-300">{t('contact.form.name')}<span className="text-red-400 ml-0.5" aria-hidden="true">*</span></Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="mt-1.5 bg-white/5 border-gray-800 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">{t('contact.form.email')}<span className="text-red-400 ml-0.5" aria-hidden="true">*</span></Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="mt-1.5 bg-white/5 border-gray-800 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">{t('contact.form.company')}<span className="text-red-400 ml-0.5" aria-hidden="true">*</span></Label>
                    <Input
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      className="mt-1.5 bg-white/5 border-gray-800 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">{t('contact.form.team_size')}<span className="text-red-400 ml-0.5" aria-hidden="true">*</span></Label>
                    <select
                      value={formData.teamSize}
                      onChange={(e) => setFormData({...formData, teamSize: e.target.value})}
                      className="mt-1.5 w-full px-3 py-2 bg-white/5 border border-gray-800 rounded-lg text-white"
                      required
                    >
                      <option value="">{t('contact.form.team_size_placeholder')}</option>
                      <option value="1-10">1-10</option>
                      <option value="11-50">11-50</option>
                      <option value="51-200">51-200</option>
                      <option value="201-500">201-500</option>
                      <option value="500+">500+</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-300">{t('contact.form.message')}<span className="text-red-400 ml-0.5" aria-hidden="true">*</span></Label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="mt-1.5 w-full px-3 py-2 bg-white/5 border border-gray-800 rounded-lg text-white h-24"
                      required
                    />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-60">
                    {loading ? t('contact.form.sending') : t('contact.form.submit')}
                    {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
